import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import {
  CreateJobNoteRequest,
  ListJobNotesRequest,
  ArchiveJobNoteRequest,
  type JobNote as JobNoteProto,
} from '@taxfyle/api-internal/internal/job_note_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { orderBy } from 'lodash-es'
import { type Observable } from 'rxjs'
import { map, share, tap } from 'rxjs/operators'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { collection } from '@/infra/collection'
import { type JobNoteProps, JobNote } from './JobNote'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { safeTimestampToDate } from '@/utils/grpc-util'
import { filterNonNil } from '@/utils/rx-util'

/**
 * A realtime event for a job note.
 */
export type JobNoteRealtimeEvent = JobNoteCreatedEvent | JobNoteArchivedEvent

/**
 * A note was created on a job.
 */
export interface JobNoteCreatedEvent {
  type: 'JobNoteCreated'
  jobId: Id
  noteId: Id
}

/**
 * A note was archived on a job.
 */
export interface JobNoteArchivedEvent {
  type: 'JobNoteArchived'
  jobId: Id
  noteId: Id
}

export interface ListJobNotesOptions extends Partial<CursorPageQuery> {
  jobId: Id
}

export interface CreateJobNoteOptions {
  jobId: Id
  body: string
  recipientUserPublicIds?: Id[]
}

/**
 * Store for job notes.
 */
export class JobNoteStore {
  readonly jobNotes = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new JobNote(),
  })

  readonly events$: Observable<JobNoteRealtimeEvent>

  constructor(
    private readonly _api: JobsApi,
    private readonly _realtime$: Observable<RealtimeEvent>,
    private readonly _primeMember: PrimeMemberByPublicIdFn,
  ) {
    this.events$ = this._realtime$.pipe(
      map((e) => this._mapJobNoteRealtimeEvent(e)),
      filterNonNil(),
      tap((e) => {
        if (e.type === 'JobNoteArchived') {
          this.removeNoteById(e.noteId)
        }
      }),
      share(),
    )

    this.events$.subscribe()
  }

  /**
   * Returns loaded notes. Newest first
   */
  notesForJob(jobId: Id) {
    return orderBy(
      this.jobNotes.filter((n) => n.jobId === jobId),
      (n) => n.dateCreated,
      'desc',
    )
  }

  /**
   * Fetches a page of active notes.
   */
  async listJobNotes(
    { jobId, limit, after }: ListJobNotesOptions,
    signal?: AbortSignal,
  ): Promise<CursorPaged<JobNote>> {
    const request = new ListJobNotesRequest()
    request.setJobId(Id.assert(jobId))
    request.setPageSize(limit ?? 20)
    if (after) {
      request.setPageToken(after)
    }
    const response = await this._api.jobNotes.listJobNotes(request)
    signal?.throwIfAborted()

    return {
      data: this.jobNotes.set(
        response
          .getJobNotesList()
          .map((note) => this._mapJobNoteProtoToProps(note)),
      ),
      next: response.getNextPageToken() || null,
    }
  }

  /**
   * Creates a note.
   */
  async createJobNote({
    jobId,
    body,
    recipientUserPublicIds = [],
  }: CreateJobNoteOptions) {
    const request = new CreateJobNoteRequest()
    request.setJobId(Id.assert(jobId))
    request.setBody(body)
    request.setRecipientUserPublicIdsList(recipientUserPublicIds.map(Id.assert))

    await this._api.jobNotes.createJobNote(request)
  }

  /**
   * Archives a note.
   */
  async archive(noteId: Id) {
    const request = new ArchiveJobNoteRequest()
    request.setJobNoteId(Id.assert(noteId))

    await this._api.jobNotes.archiveJobNote(request)

    this.removeNoteById(noteId)
  }

  /**
   * Drops a note from the local collection.
   */
  removeNoteById(noteId: Id) {
    this.jobNotes.remove(Id.assert(noteId))
  }

  private _mapJobNoteProtoToProps(proto: JobNoteProto): JobNoteProps {
    return {
      id: Id(proto.getId()),
      jobId: Id(proto.getJobId()),
      workspaceId: Id(proto.getWorkspaceId()),
      body: proto.getBody(),
      author: this._primeMember({
        workspaceId: proto.getWorkspaceId(),
        userPublicId: proto.getCreatedByUserPublicId(),
      }),
      recipientUserPublicIds: proto.getRecipientUserPublicIdsList(),
      dateCreated: safeTimestampToDate(proto.getCreateTime()),
    }
  }

  /**
   * Maps a realtime proto event to a job note domain event.
   */
  private _mapJobNoteRealtimeEvent(
    event: RealtimeEvent,
  ): JobNoteRealtimeEvent | null {
    switch (event.getTypeCase()) {
      case RealtimeEvent.TypeCase.JOB_NOTE_CREATED: {
        const payload = event.getJobNoteCreated()
        const jobId = payload?.getJobId()
        const noteId = payload?.getId()
        if (!jobId || !noteId) {
          return null
        }
        return {
          type: 'JobNoteCreated',
          jobId: Id.assert(jobId),
          noteId: Id.assert(noteId),
        }
      }
      case RealtimeEvent.TypeCase.JOB_NOTE_ARCHIVED: {
        const payload = event.getJobNoteArchived()
        const jobId = payload?.getJobId()
        const noteId = payload?.getId()
        if (!jobId || !noteId) {
          return null
        }
        return {
          type: 'JobNoteArchived',
          jobId: Id.assert(jobId),
          noteId: Id.assert(noteId),
        }
      }
    }

    return null
  }
}
